import { useCallback, useRef, useState } from "react";
import { UploadSimpleIcon, ImageIcon, XIcon } from "@phosphor-icons/react";

export default function FileUploader({ file, onChange }) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);

    const handleFiles = useCallback(
        (f) => {
            if (!f) return;
            if (!/^image\/(jpeg|jpg|png|webp)$/i.test(f.type)) {
                onChange?.(null, "请上传 JPG、PNG 或 WEBP 格式的图片。");
                return;
            }
            if (f.size > 10 * 1024 * 1024) {
                onChange?.(null, "图片太大了，最大 10MB。");
                return;
            }
            const url = URL.createObjectURL(f);
            setPreviewUrl((old) => {
                if (old) URL.revokeObjectURL(old);
                return url;
            });
            onChange?.(f, null);
        },
        [onChange],
    );

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer.files?.[0];
        handleFiles(f);
    };

    const clear = (e) => {
        e.stopPropagation();
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        onChange?.(null, null);
        if (inputRef.current) inputRef.current.value = "";
    };

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ")
                    inputRef.current?.click();
            }}
            onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid="upload-dropzone"
            className={[
                "relative w-full rounded-3xl border-2 border-dashed",
                "transition-all duration-300 cursor-pointer overflow-hidden",
                dragOver
                    ? "border-white/40 bg-white/[0.06]"
                    : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]",
                previewUrl ? "p-0 aspect-[4/5]" : "p-10 sm:p-14",
            ].join(" ")}
        >
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                data-testid="upload-input"
                onChange={(e) => handleFiles(e.target.files?.[0])}
            />

            {previewUrl ? (
                <>
                    <img
                        src={previewUrl}
                        alt="preview"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <button
                        type="button"
                        onClick={clear}
                        data-testid="clear-upload"
                        className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/70 backdrop-blur-sm text-white text-xs px-3 py-1.5 border border-white/10 hover:bg-black/80"
                    >
                        <XIcon size={14} weight="bold" />
                        移除
                    </button>
                </>
            ) : (
                <div className="flex flex-col items-center justify-center text-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center">
                        <UploadSimpleIcon
                            size={24}
                            weight="duotone"
                            className="text-white"
                        />
                    </div>
                    <div className="space-y-1">
                        <p className="font-heading text-lg text-white">
                            点击上传你的照片
                        </p>
                        <p className="text-sm text-white/50 flex items-center justify-center gap-1.5">
                            <ImageIcon size={14} weight="duotone" /> JPG · PNG · WEBP · 最大 10MB
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}
